import React from 'react';
import { CodeBlock } from './CodeBlock';

// Markdown renders a safe subset of GitHub-flavored markdown used by blog posts
// and tool descriptions. It is deliberately dependency-free (no react-markdown)
// and deterministic, so the same markup renders on the server prerender and the
// first client render — hydration-safe. Supported: ATX headings, paragraphs,
// unordered/ordered lists, fenced code (via CodeBlock), GFM tables, blockquotes,
// horizontal rules, images (block figures + inline), and inline
// bold/italic/code/links. Raw HTML is NOT rendered.

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'code'; lang: string; code: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'quote'; text: string }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'figure'; src: string; alt: string }
  | { kind: 'hr' };

// A line that is nothing but an image: render it as a block <figure> with the
// alt text as a caption (the common case for blog-post figures).
const FIGURE_RE = /^\s*!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  const isTableSep = (s: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(s);
  const splitRow = (s: string) =>
    s.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    // Fenced code block
    const fence = line.match(/^```(.*)$/);
    if (fence) {
      const lang = fence[1].trim();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        code.push(lines[i]);
        i++;
      }
      i++; // closing fence
      blocks.push({ kind: 'code', lang, code: code.join('\n') });
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      blocks.push({ kind: 'hr' });
      i++;
      continue;
    }

    // Standalone image -> block figure
    const fig = line.match(FIGURE_RE);
    if (fig) {
      blocks.push({ kind: 'figure', alt: fig[1].trim(), src: fig[2] });
      i++;
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2].trim() });
      i++;
      continue;
    }

    // Table (header row followed by a separator row)
    if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const header = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ kind: 'quote', text: buf.join(' ') });
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const item = [lines[i].replace(/^\s*[-*]\s+/, '')];
        i++;
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*]|\d+\.)\s+/.test(lines[i]) && !/^\s*(```|#{1,6}\s|>\s?)/.test(lines[i])) {
          item.push(lines[i].trim());
          i++;
        }
        items.push(item.join(' '));
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const item = [lines[i].replace(/^\s*\d+\.\s+/, '')];
        i++;
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*]|\d+\.)\s+/.test(lines[i]) && !/^\s*(```|#{1,6}\s|>\s?)/.test(lines[i])) {
          item.push(lines[i].trim());
          i++;
        }
        items.push(item.join(' '));
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    // Paragraph (consume until blank line or next block starter)
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^```/.test(lines[i]) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !FIGURE_RE.test(lines[i]) &&
      !(lines[i].includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i++;
    }
    if (para.length) blocks.push({ kind: 'paragraph', text: para.join(' ') });
  }

  return blocks;
}

// Inline renderer: bold, italic, inline code, and links. Splits on tokens and
// returns React nodes so we never inject raw HTML for user-ish content.
function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re = /(!\[[^\]]*\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|\*[^*]+\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyBase}-${n++}`;
    if (tok.startsWith('![')) {
      const im = tok.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (im) {
        nodes.push(
          <img key={key} src={im[2]} alt={im[1]} loading="lazy" className="inline-block max-w-full rounded" />,
        );
      } else {
        nodes.push(tok);
      }
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={key} className="font-semibold text-white">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      nodes.push(
        <code key={key} className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[0.85em] text-white/90">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith('[')) {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm) {
        const external = /^https?:\/\//.test(lm[2]);
        nodes.push(
          <a
            key={key}
            href={lm[2]}
            {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
            className="font-medium text-sky-400 underline-offset-2 hover:underline"
          >
            {lm[1]}
          </a>,
        );
      } else {
        nodes.push(tok);
      }
    } else if (tok.startsWith('*')) {
      nodes.push(<em key={key} className="italic text-white/80">{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export const Markdown: React.FC<{ source: string; className?: string }> = ({ source, className }) => {
  const blocks = React.useMemo(() => parseBlocks(source), [source]);

  return (
    <div className={className}>
      {blocks.map((b, idx) => {
        const key = `b-${idx}`;
        switch (b.kind) {
          case 'heading': {
            const cls =
              b.level <= 1
                ? 'mt-10 mb-4 text-3xl font-bold tracking-tight text-white'
                : b.level === 2
                  ? 'mt-9 mb-3 text-2xl font-bold tracking-tight text-white'
                  : 'mt-7 mb-2 text-lg font-semibold text-white';
            const id = b.text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            if (b.level <= 1) return <h1 key={key} id={id} className={cls}>{renderInline(b.text, key)}</h1>;
            if (b.level === 2) return <h2 key={key} id={id} className={cls}>{renderInline(b.text, key)}</h2>;
            return <h3 key={key} id={id} className={cls}>{renderInline(b.text, key)}</h3>;
          }
          case 'paragraph':
            return <p key={key} className="my-4 leading-7 text-white/72">{renderInline(b.text, key)}</p>;
          case 'code':
            return <CodeBlock key={key} code={b.code} language={b.lang} className="my-5" />;
          case 'ul':
            return (
              <ul key={key} className="my-4 list-disc space-y-1.5 pl-6 text-white/72 marker:text-white/30">
                {b.items.map((it, j) => <li key={j} className="leading-7">{renderInline(it, `${key}-${j}`)}</li>)}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="my-4 list-decimal space-y-1.5 pl-6 text-white/72 marker:text-white/40">
                {b.items.map((it, j) => <li key={j} className="leading-7">{renderInline(it, `${key}-${j}`)}</li>)}
              </ol>
            );
          case 'quote':
            return (
              <blockquote key={key} className="my-5 border-l-2 border-white/20 pl-4 text-white/60 italic">
                {renderInline(b.text, key)}
              </blockquote>
            );
          case 'figure':
            return (
              <figure key={key} className="my-6">
                <img
                  src={b.src}
                  alt={b.alt}
                  loading="lazy"
                  className="mx-auto max-w-full rounded-xl border border-white/10"
                />
                {b.alt ? (
                  <figcaption className="mt-2 text-center text-xs text-white/45">{b.alt}</figcaption>
                ) : null}
              </figure>
            );
          case 'hr':
            return <hr key={key} className="my-8 border-white/10" />;
          case 'table':
            return (
              <div key={key} className="my-6 overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-white/[0.04]">
                      {b.header.map((h, j) => (
                        <th key={j} className="border-b border-white/10 px-3 py-2.5 text-left font-semibold text-white">
                          {renderInline(h, `${key}-h-${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, r) => (
                      <tr key={r} className="border-b border-white/5 last:border-0">
                        {row.map((c, j) => (
                          <td key={j} className="px-3 py-2.5 align-top text-white/70">
                            {renderInline(c, `${key}-${r}-${j}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};
